import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box } from '@mui/material';
import { OrganizationDTO } from '../types/organization';

interface EditOrganizationModalProps {
  open: boolean;
  onClose: () => void;
  organization: OrganizationDTO | null;
  onSave: (updatedOrganization: OrganizationDTO) => void;
}

const EditOrganizationModal: React.FC<EditOrganizationModalProps> = ({
  open,
  onClose,
  organization,
  onSave,
}) => {
  const [formData, setFormData] = useState<OrganizationDTO | null>(null);

  useEffect(() => {
    if (organization) {
      setFormData({
        ...organization,
        phoneNumber: organization.phoneNumber || '',
        bankAccountNumber: organization.bankAccountNumber || '',
        correspondentAccount: organization.correspondentAccount || '',
        bankName: organization.bankName || '',
        bic: organization.bic || '',
        contactPersonFullName: organization.contactPersonFullName || '',
      });
    } else {
      // Reset form if no organization is provided (e.g., when modal is closed and reopened for a new org)
      setFormData(null);
    }
  }, [organization]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  const handleSave = () => {
    if (formData) {
      onSave(formData);
    }
    onClose();
  };

  if (!formData) return null; // Или отобразить загрузку/ошибку

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Редактировать информацию об организации</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ '& .MuiTextField-root': { m: 1, width: '100%' } }}>
          <TextField
            name="name"
            label="Наименование предприятия"
            value={formData.name || ''}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="legalAddress"
            label="Фактический адрес"
            value={formData.legalAddress || ''}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="phoneNumber"
            label="Телефон"
            value={formData.phoneNumber || ''}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="directorFullName"
            label="ФИО директора"
            value={formData.directorFullName || ''}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="contactPersonFullName"
            label="Контактное лицо"
            value={formData.contactPersonFullName || ''}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="inn"
            label="ИНН"
            value={formData.inn || ''}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="kpp"
            label="КПП"
            value={formData.kpp || ''}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="ogrn"
            label="ОГРН"
            value={formData.ogrn || ''}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="bankAccountNumber"
            label="Номер расчетного счета"
            value={formData.bankAccountNumber || ''}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="correspondentAccount"
            label="Корреспондентский счет"
            value={formData.correspondentAccount || ''}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="bankName"
            label="Наименование банка"
            value={formData.bankName || ''}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="bic"
            label="БИК"
            value={formData.bic || ''}
            onChange={handleChange}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">Отмена</Button>
        <Button onClick={handleSave} color="primary" variant="contained">Сохранить</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrganizationModal;
